import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "../config";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import StyledSelect from "./StyledSelect";

// 1 crédito = 100.000 tokens (PRECIFICAÇÃO.md)
const TOKENS_PER_CREDIT = 100000;
// Créditos de referência por recarga de R$ 500 (a definir no PRECIFICAÇÃO)
const CREDITS_PER_RECHARGE = 50;

const MetricsDashboard = ({ isAdmin = false }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [consumptionData, setConsumptionData] = useState([]);
  const [creditsBalance, setCreditsBalance] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // Mês atual (1-12)
  const [totalsByMonth, setTotalsByMonth] = useState({});
  const [rechargeCredits, setRechargeCredits] = useState("");
  const [rechargeMessage, setRechargeMessage] = useState("");

  // Mensagem de sucesso e refetch de créditos ao retornar do checkout de recarga
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutSuccess = params.get("checkout") === "credits_success";
    const clientIdFromUrl = params.get("clientId");
    if (checkoutSuccess && clientIdFromUrl) {
      window.history.replaceState({}, "", window.location.pathname);
      setSelectedClient(clientIdFromUrl);
      setRechargeMessage("Pagamento concluído! Créditos creditados.");
      axios.get(`${API_BASE}/api/credits?clientId=${clientIdFromUrl}`)
        .then(res => setCreditsBalance(res.data.creditsBalance ?? 0))
        .catch(() => {});
    }
  }, []);
  const [forecast, setForecast] = useState(null);

  // Buscar lista de clientes
  useEffect(() => {
    axios.get(`${API_BASE}/api/clients`)
      .then(response => setClients(response.data))
      .catch(error => console.error("Erro ao buscar clientes:", error));
  }, []);

  // Buscar dados de consumo, créditos e previsão quando um cliente for selecionado
  useEffect(() => {
    if (selectedClient) {
      axios.get(`${API_BASE}/api/consumption?clientId=${selectedClient}&month=${selectedMonth}`)
        .then(response => {
          setConsumptionData(response.data.data || []);
        })
        .catch(error => console.error("Erro ao buscar consumo:", error));
      axios.get(`${API_BASE}/api/credits?clientId=${selectedClient}`)
        .then(response => setCreditsBalance(response.data.creditsBalance ?? 0))
        .catch(() => setCreditsBalance(null));
      axios.get(`${API_BASE}/api/consumption/forecast?clientId=${selectedClient}&days=7`)
        .then(response => setForecast(response.data))
        .catch(() => setForecast(null));
      fetchHistoricalData(selectedClient);
    } else {
      setCreditsBalance(null);
      setForecast(null);
    }
  }, [selectedClient, selectedMonth]);

  // Função para buscar dados históricos
  const fetchHistoricalData = async (clientId) => {
    try {
      const currentMonth = new Date().getMonth() + 1; // 1-12
      const currentYear = new Date().getFullYear();
      const monthsToFetch = 6; // Últimos 6 meses
      
      const totals = {};
      
      // Buscar dados dos últimos 6 meses
      for (let i = 0; i < monthsToFetch; i++) {
        // Calcular mês e ano (recuando i meses a partir do atual)
        let targetMonth = currentMonth - i;
        let targetYear = currentYear;
        
        // Ajustar para meses anteriores ao atual ano
        if (targetMonth <= 0) {
          targetMonth += 12;
          targetYear -= 1;
        }
        
        try {
          console.log(`Buscando dados para ${targetMonth}/${targetYear}`);
          const response = await axios.get(
            `${API_BASE}/api/consumption?clientId=${clientId}&month=${targetMonth}&year=${targetYear}`
          );
          
          if (response.data && response.data.data) {
            const monthData = response.data.data;
            console.log(`Dados encontrados para ${targetMonth}/${targetYear}:`, monthData.length);
            
            // Calcular o consumo total do mês
            const total = monthData.reduce((sum, entry) => sum + (entry.consumption||0), 0);
            console.log(`Total calculado para ${targetMonth}/${targetYear}: ${total}`);
            
            if (total > 0) {
              totals[`${targetYear}-${targetMonth}`] = total;
            }
          }
        } catch (err) {
          console.log(`Sem dados para mês ${targetMonth}/${targetYear}:`, err.message);
        }
      }
      
      console.log("Totais por mês:", totals);
      setTotalsByMonth(totals);
    } catch (error) {
      console.error("Erro ao buscar dados históricos:", error);
    }
  };

  // Total de tokens do mês selecionado → créditos consumidos (para histórico)
  const totalTokensThisMonth = consumptionData.reduce((sum, entry) => sum + (entry.consumption || 0), 0);
  const creditsThisMonth = Math.round((totalTokensThisMonth / TOKENS_PER_CREDIT) * 100) / 100;
  // Saldo real do Client.creditsBalance (debitado a cada mensagem)
  const creditsRemaining = creditsBalance != null ? creditsBalance : "-";
  const valuePerCredit = 500 / CREDITS_PER_RECHARGE;
  const balanceReais = creditsBalance != null ? creditsBalance * valuePerCredit : 0;

  const handleRecharge = () => {
    const amount = parseFloat(rechargeCredits);
    if (isNaN(amount) || amount <= 0 || !selectedClient) {
      setRechargeMessage("Informe um valor válido de créditos.");
      return;
    }
    setRechargeMessage("");
    axios.post(`${API_BASE}/api/credits/recharge`, { clientId: selectedClient, credits: amount })
      .then(res => {
        setCreditsBalance(res.data.creditsBalance);
        setRechargeCredits("");
        setRechargeMessage(`Recarga concluída. Novo saldo: ${res.data.creditsBalance.toFixed(2)} créditos.`);
      })
      .catch(err => setRechargeMessage(err.response?.data?.error || "Erro ao recarregar."));
  };

  const handleBuyCredits = () => {
    const amount = parseFloat(rechargeCredits);
    if (isNaN(amount) || amount <= 0 || !selectedClient) {
      setRechargeMessage("Informe um valor válido de créditos para comprar.");
      return;
    }
    setRechargeMessage("");
    axios.post(`${API_BASE}/api/checkout/create-recharge-session`, { clientId: selectedClient, credits: amount })
      .then(res => {
        if (res.data && res.data.url) {
          window.location.href = res.data.url;
        } else {
          setRechargeMessage("Resposta inválida do servidor.");
        }
      })
      .catch(err => setRechargeMessage(err.response?.data?.error || "Erro ao iniciar pagamento. Tente novamente."));
  };

  // Configuração do gráfico (eixo em créditos)
  const chartData = {
    labels: consumptionData.map(entry => `Dia ${entry.day}`),
    datasets: [
      {
        label: "Consumo (créditos)",
        data: consumptionData.map(entry => (entry.consumption || 0) / TOKENS_PER_CREDIT),
        borderColor: "blue",
        backgroundColor: "rgba(0, 0, 255, 0.2)",
        fill: true,
      },
    ],
  };

  // Função para obter o nome do mês
  const getMonthName = (month) => {
    const monthNames = [
      "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
      "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ];
    return monthNames[month - 1];
  };

  // Função para formatar números com separadores de milhares (apenas na parte inteira)
  const formatNumber = (num, decimals = 2) => {
    const rounded = typeof num === "number" ? Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals) : num;
    const [intPart, decPart] = rounded.toString().split(".");
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return decPart !== undefined ? `${formattedInt},${decPart}` : formattedInt;
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Dashboard de Consumo</h2>

      {/* Card de saldo: Saldo real (creditsBalance) + Recarga */}
      {selectedClient && (
        <div style={styles.balanceCard}>
          <div style={styles.balanceRow}>
            <span style={styles.balanceLabel}>Saldo de Créditos</span>
            <span style={styles.balanceValue}>
              {creditsRemaining === "-" ? "—" : formatNumber(creditsRemaining)} créditos
            </span>
          </div>
          <div style={styles.balanceRow}>
            <span style={styles.balanceLabel}>Equivalente em R$</span>
            <span style={styles.balanceValue}>
              R$ {creditsBalance != null ? (balanceReais).toFixed(2).replace('.', ',') : "—"}
            </span>
          </div>
          <div style={styles.balanceRow}>
            <span style={styles.balanceLabel}>Créditos consumidos (mês)</span>
            <span style={styles.balanceValue}>{formatNumber(creditsThisMonth)} créditos</span>
          </div>
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="number"
              min="0"
              step="0.5"
              placeholder="Créditos"
              value={rechargeCredits}
              onChange={(e) => setRechargeCredits(e.target.value)}
              style={{ padding: 8, width: 100, borderRadius: 4, border: '1px solid #444' }}
            />
            <button onClick={handleBuyCredits} style={styles.button}>
              Comprar créditos
            </button>
            {isAdmin && (
              <button onClick={handleRecharge} style={{ ...styles.button, background: 'rgba(0,0,0,0.3)', opacity: 0.9 }}>
                Recarga manual (admin)
              </button>
            )}
            {rechargeCredits && parseFloat(rechargeCredits) > 0 && (
              <span style={{ color: '#B0BEC5', fontSize: 13 }}>
                ≈ R$ {(parseFloat(rechargeCredits) * valuePerCredit).toFixed(2).replace('.', ',')}
              </span>
            )}
            {rechargeMessage && <span style={{ color: rechargeMessage.includes('Erro') ? '#f44' : '#4a4', fontSize: 14 }}>{rechargeMessage}</span>}
          </div>
        </div>
      )}

      {/* Uso estimado no mês (análise preditiva — seção 7.4.1) */}
      {selectedClient && forecast && (
        <div style={styles.forecastCard}>
          <h3 style={styles.forecastTitle}>Uso estimado no mês</h3>
          {forecast.hasEnoughData ? (
            <>
              <p style={styles.forecastText}>
                Nos últimos <strong>{forecast.daysUsed}</strong> dias você usou <strong>{formatNumber(forecast.creditsInPeriod)}</strong> créditos.
              </p>
              <p style={styles.forecastText}>
                Uso estimado em 30 dias: <strong>{formatNumber(forecast.estimatedCredits30Days)}</strong> créditos (≈ <strong>R$ {forecast.estimatedReais.toFixed(2).replace('.', ',')}</strong>).
              </p>
              {forecast.conversationCount != null && forecast.conversationCount > 0 && forecast.averageCreditsPerConversation != null && (
                <p style={styles.forecastText}>
                  Média de créditos por conversa: <strong>{formatNumber(forecast.averageCreditsPerConversation)}</strong> créditos
                  {forecast.conversationCount !== undefined && (
                    <span style={{ fontWeight: 'normal', color: '#666' }}> ({forecast.conversationCount} conversa{forecast.conversationCount !== 1 ? 's' : ''} no período)</span>
                  )}
                </p>
              )}
            </>
          ) : (
            <p style={styles.forecastText}>Com mais alguns dias de uso, exibiremos a projeção mensal.</p>
          )}
        </div>
      )}

      {/* Dropdown para selecionar cliente */}
      <div style={styles.selectGroup}>
        <label style={styles.label}>Selecione um Cliente:</label>
        <StyledSelect
          value={selectedClient}
          onChange={setSelectedClient}
          options={[
            { value: '', label: 'Escolha um cliente' },
            ...clients.map(client => ({ value: client.id, label: client.name }))
          ]}
          placeholder="Escolha um cliente"
        />
      </div>

      {selectedClient && (
        <div style={styles.selectGroup}>
          <label style={styles.label}>Selecione o Mês:</label>
          <StyledSelect
            value={String(selectedMonth)}
            onChange={(v) => setSelectedMonth(parseInt(v, 10))}
            options={Array.from({ length: 12 }, (_, i) => i + 1).map(month => ({
              value: String(month),
              label: getMonthName(month)
            }))}
            placeholder="Mês"
          />
        </div>
      )}

      {/* Exibir gráfico se houver dados */}
      {selectedClient && (
        <div style={styles.chartContainer}>
          <h3 style={styles.subtitle}>
            Consumo (créditos) em {getMonthName(selectedMonth)}
          </h3>
          
          {consumptionData.length > 0 ? (
            <div style={styles.chartWrapper}>
              <Line data={chartData} options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: false,
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Créditos'
                    }
                  },
                  x: {
                    title: {
                      display: true,
                      text: 'Dia do Mês'
                    }
                  }
                }
              }} />
            </div>
          ) : (
            <p style={styles.noData}>Nenhum dado de consumo disponível para este mês.</p>
          )}
        </div>
      )}

      {/* Resumo histórico */}
      {selectedClient && Object.keys(totalsByMonth).length > 0 && (
        <div style={styles.historySummary}>
          <h3 style={styles.subtitle}>Resumo Histórico de Consumo</h3>
          <div style={styles.historyGrid}>
            {Object.entries(totalsByMonth)
              .sort((a, b) => {
                // Ordenar do mais recente para o mais antigo
                return b[0].localeCompare(a[0]);
              })
              .map(([yearMonth, total]) => {
                const [year, month] = yearMonth.split('-').map(Number);
                return (
                  <div key={yearMonth} style={styles.historyItem}>
                    <span style={styles.historyMonth}>{getMonthName(month)} {year}:</span>
                    <span style={styles.historyValue}>{formatNumber(Math.round((total / TOKENS_PER_CREDIT) * 100) / 100)} créditos</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Vídeo de ajuda - Dashboard */}
      <section style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid rgba(0, 212, 255, 0.2)' }}>
        <h3 style={{ color: '#00D4FF', fontSize: '1rem', fontWeight: 600, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Vídeo de ajuda</h3>
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', maxWidth: 800, margin: '0 auto', borderRadius: 12, border: '1px solid rgba(0, 212, 255, 0.3)' }}>
          <iframe
            title="Como usar o Dashboard"
            src="https://www.loom.com/embed/35d7a16e351e473585659db553cbd74d"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            frameBorder="0"
            allow="fullscreen"
            allowFullScreen
          />
        </div>
      </section>
    </div>
  );
};

// Estilos futuristas para o componente
const styles = {
  container: {
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    padding: '30px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  title: {
    textAlign: 'center',
    marginBottom: '30px',
    color: '#FFFFFF',
    fontSize: '2rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    background: 'linear-gradient(135deg, #B026FF 0%, #00D4FF 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    textShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
  },
  subtitle: {
    margin: '25px 0 20px',
    color: '#00D4FF',
    fontSize: '1.3rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  balanceCard: {
    marginBottom: '30px',
    padding: '24px 28px',
    background: 'linear-gradient(135deg, rgba(10, 14, 39, 0.95) 0%, rgba(0, 212, 255, 0.15) 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(0, 212, 255, 0.4)',
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 212, 255, 0.08)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px',
    alignItems: 'center',
  },
  balanceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  balanceLabel: {
    color: '#B0BEC5',
    fontSize: '14px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  balanceValue: {
    color: '#00D4FF',
    fontSize: '1.25rem',
    fontWeight: '700',
    textShadow: '0 0 12px rgba(0, 212, 255, 0.4)',
  },
  button: {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid rgba(0, 212, 255, 0.5)',
    background: 'linear-gradient(135deg, #00D4FF 0%, #B026FF 100%)',
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer',
  },
  forecastCard: {
    marginBottom: '30px',
    padding: '24px 28px',
    background: 'linear-gradient(135deg, rgba(176, 38, 255, 0.12) 0%, rgba(10, 14, 39, 0.95) 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(176, 38, 255, 0.35)',
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.3), 0 0 20px rgba(176, 38, 255, 0.08)',
  },
  forecastTitle: {
    color: '#B026FF',
    fontSize: '1.1rem',
    fontWeight: 600,
    marginTop: 0,
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  forecastText: {
    color: '#B0BEC5',
    fontSize: '15px',
    margin: '8px 0',
    lineHeight: 1.5,
  },
  selectGroup: {
    marginBottom: '25px',
  },
  label: {
    display: 'block',
    marginBottom: '10px',
    fontWeight: '600',
    color: '#B0BEC5',
    fontSize: '14px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  select: {
    width: '100%',
    padding: '12px 15px',
    borderRadius: '8px',
    border: '1px solid rgba(0, 212, 255, 0.3)',
    fontSize: '16px',
    background: 'rgba(10, 14, 39, 0.8)',
    color: '#FFFFFF',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
  },
  chartContainer: {
    marginTop: '30px',
    background: 'linear-gradient(135deg, rgba(10, 14, 39, 0.9) 0%, rgba(176, 38, 255, 0.4) 100%)',
    padding: '30px',
    borderRadius: '15px',
    border: '1px solid rgba(0, 212, 255, 0.3)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 0 30px rgba(0, 212, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    position: 'relative',
    overflow: 'hidden',
  },
  chartWrapper: {
    height: '350px',
    position: 'relative',
    zIndex: 1,
  },
  noData: {
    textAlign: 'center',
    padding: '30px',
    background: 'rgba(10, 14, 39, 0.6)',
    borderRadius: '10px',
    color: '#B0BEC5',
    border: '1px dashed rgba(0, 212, 255, 0.3)',
  },
  historySummary: {
    marginTop: '30px',
    background: 'linear-gradient(135deg, rgba(10, 14, 39, 0.9) 0%, rgba(176, 38, 255, 0.4) 100%)',
    padding: '30px',
    borderRadius: '15px',
    border: '1px solid rgba(0, 212, 255, 0.3)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 0 30px rgba(0, 212, 255, 0.1)',
    backdropFilter: 'blur(10px)',
  },
  historyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
  },
  historyItem: {
    padding: '20px',
    background: 'rgba(176, 38, 255, 0.3)',
    borderRadius: '10px',
    border: '1px solid rgba(0, 212, 255, 0.2)',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
  },
  historyMonth: {
    fontWeight: '600',
    color: '#FFFFFF',
    fontSize: '14px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  historyValue: {
    color: '#00D4FF',
    fontWeight: '700',
    fontSize: '16px',
    textShadow: '0 0 10px rgba(0, 212, 255, 0.5)',
  }
};

export default MetricsDashboard;
